import { X } from 'lucide-react';

export type HeroAlertTone = 'invited' | 'error';

export interface HeroAlertProps {
  tone: HeroAlertTone;
  title: string;
  body: string;
  /** Renders the ✕. Dismiss only hides the alert — it must never navigate. */
  onDismiss?: () => void;
}

/**
 * The Hero's inline alert card — the `.Alerts` instance from the Welcome
 * frames (Figma 9661:98404 invited / 9708:128675 expired, and their Can view
 * twins 9706:127501 / 9708:128744).
 *
 * It is an ALERT, not a screen. The invite context and the expired-link state
 * were previously built as full-page takeovers that replaced the Hero: the
 * marketing headline became the household name, the subtitle became the
 * inviter line, and the waitlist CTA disappeared. Both frames keep the entire
 * Hero intact and slot this card between the subtitle and "Continue with
 * Google" — so the invitee still sees what Yaruta is, can still sign in, and
 * can still reach the waitlist. YAR-288/YAR-289 AC8 additionally require the
 * expired state be dismiss-only with "no routing beyond the Hero".
 *
 * Geometry is taken from the frames: full width, 8px radius, 1px border, 12px
 * padding on all four sides, 12px gap between the text column and the 24px ✕
 * (cross-axis top-aligned), 6px between title and body. Title is 16/24 medium,
 * body 14/20 regular, and BOTH use the tone's single text colour — the body is
 * not muted.
 */
const TONE_CLASSES: Record<HeroAlertTone, string> = {
  // surface/primary/default-subtle + border/primary/default-subtle + text/primary/default
  invited: 'bg-primary-light border-primary-light-border text-primary',
  // surface/error/default-subtle + border/error/default-subtle + text/error/default
  error: 'bg-danger-light border-danger-border text-danger-hover',
};

export function HeroAlert({ tone, title, body, onDismiss }: HeroAlertProps) {
  return (
    <div
      role="alert"
      className={`flex w-full items-start gap-3 rounded-lg border p-3 text-left ${TONE_CLASSES[tone]}`}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <p className="text-base font-medium leading-6">{title}</p>
        <p className="text-sm leading-5">{body}</p>
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="shrink-0 cursor-pointer"
        >
          {/* `icon/neutral/default` (#5e5e5e). The ✕ is an icon, not part of
              the tone's text run, so it does NOT take the alert colour — it
              was inheriting `text-primary` (#264133) from the container and
              turning green on the invited tone (YAR-289). */}
          <X
            size={24}
            className="text-icon-default"
            aria-hidden="true"
          />
        </button>
      )}
    </div>
  );
}
